/**
 * 푸시 알림을 눌러 들어온 경보 — 「이 경보를 보여 달라」는 요청 한 건(2026-10-19, 10-12 검증 V-22).
 *
 * 예전에는 알림을 누르면 `location.assign('/alerts')` 로 **앱 전체를 다시 띄웠다**. 다시 뜬 화면은 첫 경보
 * 목록을 「이미 본 것(기준선)」으로 흡수하므로(AlarmCenter·AlertTtsAnnouncer — 2026-08-31 낭독 사고 대책),
 * 바로 그 경보 때문에 알림을 눌렀는데 경보창도 다국어 음성도 나오지 않았다.
 *
 * 이제 알림 탭은 여기에 경보 id 를 맡기고(앱이 살아 있으면 이벤트로 바로, 처음 켜지는 중이면 기준선을 잡을 때
 * 꺼내 본다), 두 화면은 이 id 만 기준선 예외로 둔다 — 그 경보가 아직 미확인이면 창을 띄우고 읽는다.
 * 같은 경보를 두 번 읽지 않는 것은 기존 공유 중복 방지(useAlertTTS `spoken`)가 맡는다.
 *
 * 보관은 모듈 메모리 + sessionStorage(받기 전에 새로고침이 끼어도 남게 — 경보창이 받으면 지운다). 5분이 지나면
 * 버린다 — 시각은 이 단말 안에서 맡긴 때와 꺼낸 때의 차이만 보므로 기기 시계가 틀려도 상관없다.
 */

const STORAGE_KEY = "expertgas.push.focus";
const EVENT_NAME = "expertgas:push-focus";
/** 맡긴 뒤 이만큼 지나면 버린다. 콜드 스타트(로그인 복원·첫 목록)까지 넉넉히. */
const FOCUS_TTL_MS = 5 * 60_000;

interface PendingFocus {
  id: number;
  at: number;
}

let pending: PendingFocus | null = null;

function validId(v: unknown): number | null {
  const n = typeof v === "number" ? v : typeof v === "string" ? Number(v.trim()) : NaN;
  return Number.isInteger(n) && n > 0 ? n : null;
}

/**
 * 알림 data 에서 경보 id 를 꺼낸다 — 서버 `push.py::_data_block` 은 `alert_id` 를 문자열로 싣는다.
 * 안드로이드는 data 를 인텐트 extra 로, iOS 는 userInfo 로 넘기는데 둘 다 플러그인이 `notification.data` 에 담는다.
 */
export function alertIdFromPushData(data: unknown): number | null {
  if (!data || typeof data !== "object") return null;
  const d = data as Record<string, unknown>;
  return validId(d.alert_id ?? d.alertId);
}

/** 알림 탭이 고른 경보를 맡긴다. 이미 떠 있는 화면에는 이벤트로 바로 알린다. */
export function setPushFocus(id: number): void {
  const ok = validId(id);
  if (ok == null) return;
  pending = { id: ok, at: Date.now() };
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(pending));
  } catch {
    /* 저장 불가(사생활 모드 등) — 메모리만으로도 같은 실행 안에서는 된다 */
  }
  try {
    window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: { id: ok } }));
  } catch {
    /* noop */
  }
}

/**
 * 맡겨 둔 경보 id(5분 안의 것만). 꺼내도 지우지 않는다 — 경보창·낭독기 둘 다 같은 첫 렌더에서 같은 값을 봐야 한다.
 * 다 받은 뒤에는 AlarmCenter 가 {@link clearPushFocus} 로 비운다(새로고침 때 같은 경보를 또 고른 것으로 보지 않게).
 */
export function peekPushFocus(now = Date.now()): number | null {
  if (!pending) {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      const p = raw ? (JSON.parse(raw) as Partial<PendingFocus>) : null;
      const id = validId(p?.id);
      if (id != null && typeof p?.at === "number") pending = { id, at: p.at };
    } catch {
      /* noop */
    }
  }
  if (!pending) return null;
  const age = now - pending.at;
  if (age > FOCUS_TTL_MS || age < -60_000) {
    clearPushFocus();
    return null;
  }
  return pending.id;
}

/** 맡겨 둔 것을 지운다(id 를 주면 그 id 일 때만). */
export function clearPushFocus(id?: number): void {
  if (id != null && pending && pending.id !== id) return;
  pending = null;
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* noop */
  }
}

/** 앱이 살아 있는 동안 들어온 알림 탭을 구독한다. 돌려준 함수로 끊는다. */
export function onPushFocus(cb: (id: number) => void): () => void {
  if (typeof window === "undefined") return () => undefined;
  const handler = (e: Event) => {
    const id = validId((e as CustomEvent<{ id?: unknown }>).detail?.id);
    if (id != null) cb(id);
  };
  window.addEventListener(EVENT_NAME, handler);
  return () => window.removeEventListener(EVENT_NAME, handler);
}
